import get from 'lodash/get';

import { staticSchemas, StaticSchemasKey, WidgetStaticSchemas } from '../staticSchemas';

// This type represents a union of all possible valid widgets
export type ValidatedWidget = WidgetStaticSchemas[StaticSchemasKey];

const LEGACY_WIDGET_TYPES = [
  // Submit & Summary pages' widgets (ordinary components now)
  'SubmitPage',
  'SubmitPageComment',
  'SubmitPageReturnToFixSwitch',
  'SubmitPageScoreSummary',
  'SubmitPageTitle',
  'SubmitPageUnansweredQuestions',
  'SummaryPage',
  'SummaryPageScoreSummary',
  'SummaryPageTaskSubmittedNotification',
  'SummaryPageSubmissionHistory',
  'SubmitPageSatisfactionSurvey',
  // Questions' answer widgets (non-existent after question layouts refactor)
  'ClozeQuestionAnswer',
  'EvaluatablePluginAnswer',
  'FillInTheBlanksQuestionAnswer',
  'InteractivePollAnswer',
  'InteractiveQuizAnswer',
  'OpenQuestionAnswer',
  'SelectionQuestionAnswer',
  'SortQuestionAnswer'
];

export type ValidatedWidgets = Record<string, ValidatedWidget>;

const validatedCache = new WeakMap<Record<string, unknown>, ValidatedWidgets>();

export function getValidatedContentObjectsFromDocument(document: Record<string, unknown>): ValidatedWidgets {
  const cached = validatedCache.get(document);
  if (cached) return cached;

  const rawContentObjects = get(document, 'staticContent.contentObjects') as Record<string, unknown>;
  const result = getValidatedContentObjects(rawContentObjects);
  validatedCache.set(document, result);
  return result;
}

export function getValidatedContentObjects(rawContentObjects: Record<string, unknown>): ValidatedWidgets {
  const validatedContentObjects: ValidatedWidgets = {};

  Object.entries(rawContentObjects).forEach(([id, rawData]) => {
    const type = rawData instanceof Object && 'type' in rawData ? (rawData?.type as StaticSchemasKey) : undefined;
    if (!type) {
      console.error(`Validation Error:  No type provided for widget ${id}`);
      return;
    }

    if (LEGACY_WIDGET_TYPES.includes(type)) {
      return;
    }

    const schema = staticSchemas[type];
    if (!schema) {
      console.error(`Validation Error: No schema registered for type "${type}" (ID: ${id})`);
      return;
    }

    const result = schema.safeParse(rawData);

    if (result.success) {
      validatedContentObjects[id] = result.data;
    } else {
      console.error(`Validation Error in widget ${id} (${type}):`, result.error.format());
    }
  });

  return validatedContentObjects;
}
